import { useRef, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  left: React.ReactNode
  right: React.ReactNode
  defaultLeftPercent?: number
  minLeftPercent?: number
  maxLeftPercent?: number
}

/**
 * Horizontal split view with a draggable divider.
 */
export function ResizableSplitPanel({
  left,
  right,
  defaultLeftPercent = 55,
  minLeftPercent = 20,
  maxLeftPercent = 80,
}: Props) {
  const { t } = useTranslation()
  const [leftPercent, setLeftPercent] = useState(defaultLeftPercent)
  const containerRef = useRef<HTMLDivElement>(null)
  const isDragging = useRef(false)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      isDragging.current = true
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      const onMouseMove = (ev: MouseEvent) => {
        if (!isDragging.current || !containerRef.current) return
        const rect = containerRef.current.getBoundingClientRect()
        const newPercent = ((ev.clientX - rect.left) / rect.width) * 100
        setLeftPercent(Math.min(Math.max(newPercent, minLeftPercent), maxLeftPercent))
      }

      const onMouseUp = () => {
        isDragging.current = false
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    [minLeftPercent, maxLeftPercent],
  )

  return (
    <div ref={containerRef} className="flex items-start gap-0">
      <div
        style={{ width: `${leftPercent}%` }}
        className="min-w-0 shrink-0 space-y-4"
      >
        {left}
      </div>

      <div
        onMouseDown={handleMouseDown}
        className="w-3 shrink-0 self-stretch flex items-center justify-center cursor-col-resize group select-none"
        title={t('resizable_panel.drag_hint')}
      >
        <div className="w-0.5 h-full min-h-12 bg-gray-200 group-hover:bg-blue-400 active:bg-blue-500 rounded-full transition-colors duration-150" />
      </div>

      <div
        style={{ width: `${100 - leftPercent}%` }}
        className="min-w-0 shrink-0"
      >
        {right}
      </div>
    </div>
  )
}
